/**
 * Application configuration loader.
 *
 * Mirrors Portal-Ui's config.js pattern — loads config from a backend endpoint
 * (discovered via /config.json) with local override support via /config.local.json.
 *
 * Config is cached in sessionStorage with a version key to allow invalidation
 * across deployments.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MsalConfig {
  clientId: string;
  tenantId: string;
  authorityUrl: string;
  scope: string;
}

export interface SsoConfig {
  enabled: string;
  externalIDPs?: Record<string, string>;
}

export interface DataProtectConfig {
  idleTimeoutMinutes: number;
}

export interface FeaturesConfig {
  [key: string]: string | undefined;
}

export interface ApiUrlsRaw {
  baseUrl?: string;
  clinicMgmt?: string;
  /**
   * @deprecated Transitional alias for `clinicMgmt`. Remove once every
   * environment's slv2 App Config has migrated from `apiUrls:api` to
   * `apiUrls:clinicMgmt` (see configuration-deployer PR renaming the key).
   */
  api?: string;
  auth?: string;
  authv2?: string;
  accountsV2?: string;
  [key: string]: string | undefined;
}

export interface ApplicationInsightsConfig {
  connectionString?: string;
  [key: string]: string | undefined;
}

/** Raw JSON shape returned by the backend or local config file. */
interface RawConfig {
  configEndpoint?: string;
  useLocalOnly?: boolean;
  apiUrls?: ApiUrlsRaw;
  msal?: Partial<MsalConfig>;
  sso?: SsoConfig;
  dataProtect?: Partial<DataProtectConfig>;
  ApplicationInsights?: ApplicationInsightsConfig;
  features?: FeaturesConfig;
  [key: string]: unknown;
}

/** Resolved (flattened) config consumed by the rest of the application. */
export interface AppConfig {
  // Resolved API URLs
  apiBase: string;
  apiBaseAuth: string;
  apiBaseAuthV2: string;
  apiAccountsV2: string;

  // MSAL / SSO
  clientId: string;
  tenantId: string;
  authorityUrl: string;
  scope: string;
  ssoEnabled: boolean;
  externalIDPs: Record<string, string>;

  // Data protection
  dataProtect: DataProtectConfig;

  // Application Insights
  applicationInsights: ApplicationInsightsConfig;

  // Feature flags
  features: FeaturesConfig;

  // Internal bookkeeping
  _rawBackendConfig: RawConfig;
  _configVersion: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SESSION_KEY = 'clinicmgmt-ui-config';
const CONFIG_VERSION = 1; // Increment to invalidate old cached configs

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let config: AppConfig | null = null;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deepMerge<T extends Record<string, unknown>>(target: T, source: Record<string, unknown>): T {
  const result: Record<string, unknown> = { ...target };
  for (const key of Object.keys(source)) {
    const srcVal = source[key];
    const tgtVal = (target as Record<string, unknown>)[key];
    if (
      srcVal && typeof srcVal === 'object' && !Array.isArray(srcVal) &&
      tgtVal && typeof tgtVal === 'object' && !Array.isArray(tgtVal)
    ) {
      result[key] = deepMerge(
        tgtVal as Record<string, unknown>,
        srcVal as Record<string, unknown>,
      );
    } else {
      result[key] = srcVal;
    }
  }
  return result as T;
}

function normalizeExternalIDPs(
  externalIDPs: Record<string, string> | undefined | null,
): Record<string, string> {
  if (!externalIDPs || typeof externalIDPs !== 'object') {
    return {};
  }
  return Object.entries(externalIDPs).reduce<Record<string, string>>(
    (acc, [domain, value]) => {
      acc[domain.toLowerCase()] = value;
      return acc;
    },
    {},
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Load configuration from the backend (or local override).
 *
 * @param forceReload - When true, ignores in-memory and sessionStorage caches.
 */
export async function loadConfig(forceReload = false): Promise<void> {
  // Return early if already loaded (in-memory)
  if (!forceReload && config) {
    return;
  }

  // Try sessionStorage cache
  if (!forceReload && typeof window !== 'undefined' && window.sessionStorage) {
    const cached = window.sessionStorage.getItem(SESSION_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as AppConfig & { _configVersion?: number };
        if (parsed && typeof parsed === 'object') {
          if (parsed._configVersion !== CONFIG_VERSION) {
            window.sessionStorage.removeItem(SESSION_KEY);
          } else {
            config = parsed;
            return;
          }
        }
      } catch {
        // Parsing failed — load fresh
      }
    }
  }

  // ------------------------------------------------------------------
  // 1. Try local config (for local dev only)
  // ------------------------------------------------------------------
  let localConfig: RawConfig | null = null;
  if (import.meta.env.DEV) {
    try {
      const localResponse = await fetch('/config.local.json');
      if (localResponse.ok) {
        localConfig = (await localResponse.json()) as RawConfig;
        console.log('Loaded local config from config.local.json');
      }
    } catch {
      // No local config — continue with backend
    }
  }

  // ------------------------------------------------------------------
  // 2. Resolve the backend config (or use local-only)
  // ------------------------------------------------------------------
  let backendConfig: RawConfig;

  if (localConfig?.useLocalOnly) {
    backendConfig = localConfig;
  } else {
    const staticResponse = await fetch('/config.json');
    if (!staticResponse.ok) {
      throw new Error('Failed to load static config.json');
    }
    const staticConfig = (await staticResponse.json()) as { configEndpoint?: string };
    const { configEndpoint } = staticConfig;
    if (!configEndpoint) {
      throw new Error('configEndpoint is missing from config.json');
    }

    const backendResponse = await fetch(configEndpoint);
    if (!backendResponse.ok) {
      throw new Error('Failed to load backend configuration');
    }
    backendConfig = (await backendResponse.json()) as RawConfig;

    // Merge local overrides on top of backend config
    if (localConfig) {
      backendConfig = deepMerge(backendConfig, localConfig as Record<string, unknown>);
    }
  }

  // ------------------------------------------------------------------
  // 3. Flatten into AppConfig
  // ------------------------------------------------------------------
  const {
    apiUrls = {},
    dataProtect = {},
    ApplicationInsights = {},
  } = backendConfig;

  const { baseUrl = '', ...paths } = apiUrls;
  const makeUrl = (path: string | undefined): string => {
    if (!path) return baseUrl;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const trimmedBase = baseUrl.replace(/\/$/, '');
    return `${trimmedBase}${path}`;
  };

  const idleTimeoutMinutes = Number(dataProtect.idleTimeoutMinutes ?? 30);

  // Prefer the new `apiUrls.clinicMgmt` key; fall back to the legacy
  // `apiUrls.api` during the deploy window where some environments'
  // App Config has been migrated and others haven't. Warn (but don't
  // throw) if both are absent — makeUrl would silently collapse to
  // baseUrl, which 404s for every ClinicMgmt request.
  const clinicMgmtPath = paths.clinicMgmt ?? paths.api;
  if (!clinicMgmtPath) {
    console.warn(
      '[config] Neither apiUrls.clinicMgmt nor apiUrls.api is set; ClinicMgmt API calls will resolve to baseUrl and likely 404.',
    );
  }

  config = {
    // Resolved API URLs
    apiBase: makeUrl(clinicMgmtPath),
    apiBaseAuth: makeUrl(paths.auth),
    apiBaseAuthV2: makeUrl(paths.authv2),
    apiAccountsV2: makeUrl(paths.accountsV2),

    // MSAL / SSO
    clientId: backendConfig.msal?.clientId ?? '',
    tenantId: backendConfig.msal?.tenantId ?? '',
    authorityUrl: backendConfig.msal?.authorityUrl ?? '',
    scope: backendConfig.msal?.scope ?? '',
    ssoEnabled: backendConfig.sso?.enabled === 'True',
    externalIDPs: normalizeExternalIDPs(backendConfig.sso?.externalIDPs),

    // Data protection
    dataProtect: { idleTimeoutMinutes },

    // Application Insights
    applicationInsights: ApplicationInsights,

    // Feature flags
    features: backendConfig.features ?? {},

    // Internal bookkeeping
    _rawBackendConfig: backendConfig,
    _configVersion: CONFIG_VERSION,
  };

  // ------------------------------------------------------------------
  // 4. Persist to sessionStorage
  // ------------------------------------------------------------------
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(config));
    } catch {
      // Ignore storage errors (quota, disabled, etc.)
    }
  }
}

/**
 * Return the current resolved config.
 * Throws if `loadConfig()` has not been called yet.
 */
export function getConfig(): AppConfig {
  if (!config) {
    throw new Error(
      'Config has not been loaded yet. Call loadConfig() before getConfig().',
    );
  }
  return config;
}
